import { type ParseableError, isNeo4jError, isParseableError } from '@nx/errors';
import type { Neo4jError } from 'neo4j-driver-core';

import legacyNeo4jErrorDescriptions from './legacyNeo4jErrorDescriptions';

export type ExploreError = ParseableError & { ignore?: boolean };

const neo4jErrorFromGqlStatus = (
  gqlStatus: string,
  gqlStatusDescription = '',
): Pick<Neo4jError, 'gqlStatus' | 'gqlStatusDescription'> => ({
  gqlStatus,
  gqlStatusDescription,
});

const neo4jErrorFromCode = (code: string, message = ''): Pick<Neo4jError, 'name' | 'message' | 'code'> => ({
  name: 'Neo4jError',
  code,
  message,
});

export const UnknownErrors = {
  UnknownError: neo4jErrorFromCode('Unknown.UnknownError', 'An unknown error occurred.'),
} as const;

export const BoltErrors = {
  NoSession: neo4jErrorFromCode('BoltError.NoSession', 'No session'),
};

export const SearchErrors = {
  SearchPhraseInvalid: neo4jErrorFromCode(
    'SearchError.SearchPhraseInvalid',
    'Invalid Search phrase. Please check the definition of the saved Search phrase.',
  ),
  SearchPhraseMissingParams: neo4jErrorFromCode(
    'SearchError.SearchPhraseMissingParams',
    'Please fill in all required parameter values.',
  ),
  GeneratedCypherError: neo4jErrorFromCode(
    'SearchError.GeneratedCypherError',
    'Something went wrong, please try again.',
  ),
  FindShortestPathError: neo4jErrorFromCode(
    'SearchError.FindShortestPathError',
    'No query could be generated for the provided input.',
  ),
} as const;

export const PerspectiveErrors = {
  FailedToFetchRoles: neo4jErrorFromCode(
    'PerspectiveError.FailedToFetchRoles',
    'Failed to fetch roles: Invalid role name. The role is already assigned to the perspective.',
  ),
  FailedToAssignPerspective: (message: string) =>
    neo4jErrorFromCode('PerspectiveError.FailedToAssignPerspective', `Failed to assign perspective: ${message}`),
} as const;

export const getPerspectiveErrorMessage = (error: ExploreError) => {
  if (!isNeo4jError(error)) {
    return `Invalid role name: ${error.message ?? error}`;
  }

  if (ClientErrors.General.InvalidArguments.code === error.code) {
    return `Invalid role name. Please check that the name only \ncontains alphanumeric characters and the “_” character.`;
  }

  if (matchesErrorCodeOrGqlStatus(error, ClientErrors.Procedure.ProcedureNotFound.code)) {
    return "Couldn't create role.";
  }

  return `Invalid role name: ${error.gqlStatusDescription ?? error.message}`;
};

export const isPerspectiveIgnoredError = (error: ParseableError) => {
  const codes = [ClientErrors.Procedure.ProcedureCallFailed.code, ClientErrors.Security.Forbidden.code];
  return codes.some((code) => matchesErrorCodeOrGqlStatus(error, code));
};

export const NVLErrors = {
  VisualizationError: (message = '') =>
    neo4jErrorFromCode('NVLError.VisualizationError', `The visualization has encountered an error. ${message}`),
} as const;

export const ImportPerspectiveErrors = {
  FormatError: neo4jErrorFromCode('ImportPerspectiveError.FormatError', 'Loaded perspective is not of type string.'),
  ProcessingError: (message: null | string) =>
    neo4jErrorFromCode(
      'ImportPerspectiveError.ValidationError',
      message ?? 'Unknown error during perspective processing.',
    ),
} as const;

export const AuthErrors = {
  NoAuthSettingAccess: neo4jErrorFromCode(
    'AuthError.NoAuthSettingAccess',
    'Auth setting could not be read from the server',
  ),
};

export const AppStateErrors = {
  NoAccessToDatabase: neo4jErrorFromCode(
    'AppStateError.NoAccessToDatabase',
    'You need access to a database to use Neo4j Bloom. Please login with a different user or contact your system administrator for access.',
  ),
};

export const WriteAndRollbackTransactionErrors = {
  ManagedRollbackError: neo4jErrorFromCode('WriteAndRollbackTransactionError.ManagedRollbackError'),
};

export const ClientErrors = {
  Schema: {
    ConstraintValidationFailed: neo4jErrorFromCode('Neo.ClientError.Schema.ConstraintValidationFailed'),
  },
  Procedure: {
    ProcedureNotFound: neo4jErrorFromCode('Neo.ClientError.Procedure.ProcedureNotFound'),
    ProcedureCallFailed: neo4jErrorFromCode('Neo.ClientError.Procedure.ProcedureCallFailed'),
    ProcedureRegistrationFailed: neo4jErrorFromCode('Neo.ClientError.Procedure.ProcedureRegistrationFailed'),
  },
  Security: {
    Forbidden: neo4jErrorFromCode('Neo.ClientError.Security.Forbidden'),
  },
  General: {
    InvalidArguments: neo4jErrorFromCode('Neo.ClientError.General.InvalidArguments'),
  },
  Statement: {
    UnsupportedAdministrationCommand: neo4jErrorFromCode('Neo.ClientError.Statement.UnsupportedAdministrationCommand'),
  },
  Transaction: {
    // TransactionTimedOut is caused by exceeding limit defined in neo4j.conf db.transaction.timeout (pre 5.7.0 includes also driver limit)
    TransactionTimedOut: neo4jErrorFromCode('Neo.ClientError.Transaction.TransactionTimedOut'),
    // TransactionTimedOutClientConfiguration is caused by exceeding limit defined in driver TransactionConfig (from 5.7.0 on)
    TransactionTimedOutClientConfiguration: neo4jErrorFromCode(
      'Neo.ClientError.Transaction.TransactionTimedOutClientConfiguration',
    ),
    Terminated: neo4jErrorFromCode('Neo.ClientError.Transaction.Terminated'),
  },
} as const;

export const DatabaseErrors = {
  TransactionTerminatedException: neo4jErrorFromGqlStatus('25N14'),
  ProcedureException: neo4jErrorFromGqlStatus('42N08'),
  FailedToInvokeProcedure: neo4jErrorFromGqlStatus('42N01'),
  ProcedureError: neo4jErrorFromGqlStatus('51N00'),
  AuthorizationViolationException: neo4jErrorFromGqlStatus('42NFF'),
  UnsupportedAdministrationCommand: neo4jErrorFromGqlStatus('51N27'),
} as const;

export const mapErrorCodeToGqlStatus = (clientError: string): string | undefined => {
  const mapping = {
    [ClientErrors.Transaction.TransactionTimedOut.code]: DatabaseErrors.TransactionTerminatedException.gqlStatus,
    [ClientErrors.Transaction.TransactionTimedOutClientConfiguration.code]:
      DatabaseErrors.TransactionTerminatedException.gqlStatus,
    [ClientErrors.Transaction.Terminated.code]: DatabaseErrors.TransactionTerminatedException.gqlStatus,
    [ClientErrors.Security.Forbidden.code]: DatabaseErrors.AuthorizationViolationException.gqlStatus,
    [ClientErrors.Procedure.ProcedureNotFound.code]: DatabaseErrors.ProcedureException.gqlStatus,
    [ClientErrors.Procedure.ProcedureCallFailed.code]: DatabaseErrors.FailedToInvokeProcedure.gqlStatus,
    [ClientErrors.Procedure.ProcedureRegistrationFailed.code]: DatabaseErrors.ProcedureError.gqlStatus,
    [ClientErrors.Statement.UnsupportedAdministrationCommand.code]:
      DatabaseErrors.UnsupportedAdministrationCommand.gqlStatus,
  };

  return mapping[clientError];
};

export const isForbiddenError = (error: ParseableError): error is Neo4jError => {
  return matchesErrorCodeOrGqlStatus(error, ClientErrors.Security.Forbidden.code);
};

export const isProcedureNotFoundError = (error: ParseableError): error is Neo4jError => {
  return matchesErrorCodeOrGqlStatus(error, ClientErrors.Procedure.ProcedureNotFound.code);
};

export const isProcedureRegistrationFailedError = (error: ParseableError): error is Neo4jError => {
  return matchesErrorCodeOrGqlStatus(error, ClientErrors.Procedure.ProcedureRegistrationFailed.code);
};

export const isManagedRollbackError = (error: ParseableError): error is Neo4jError => {
  return isNeo4jError(error) && WriteAndRollbackTransactionErrors.ManagedRollbackError.code === error.code;
};

export const isUnsupportedAdministrationCommandError = (error: ParseableError): error is Neo4jError => {
  return matchesErrorCodeOrGqlStatus(error, ClientErrors.Statement.UnsupportedAdministrationCommand.code);
};

// todo clarify with Louise if this is correct
export const isTransactionTimeoutError = (error: ParseableError): error is Neo4jError => {
  const codes = [
    ClientErrors.Transaction.TransactionTimedOut.code,
    ClientErrors.Transaction.TransactionTimedOutClientConfiguration.code,
  ];
  return codes.some((code) => matchesErrorCodeOrGqlStatus(error, code));
};

export const isQueryAborted = (error: ParseableError) => {
  return (
    matchesErrorCodeOrGqlStatus(error, ClientErrors.Transaction.Terminated.code) ||
    error.message === 'You cannot run more transactions on a closed session.'
  );
};

const matchesErrorCodeOrGqlStatus = (error: ParseableError, code: string): boolean => {
  if (!isNeo4jError(error)) return false;
  return matchesGqlStatus({ error, gqlStatus: mapErrorCodeToGqlStatus(code) ?? '' }) || code === error.code;
};

const matchesGqlStatus = ({ error, gqlStatus }: { error: ParseableError; gqlStatus: string }): boolean => {
  if (!isNeo4jError(error)) return false;

  if (error.gqlStatus === gqlStatus) return true;

  if (error.cause && typeof error.cause === 'object') {
    return matchesGqlStatus({ error: error.cause, gqlStatus });
  }

  return false;
};

export const isAbortError = (error: ParseableError): error is ParseableError & { name: 'AbortError' } => {
  return error.name === 'AbortError';
};

export const isErrorPayload = (payload: unknown): payload is { error: ParseableError; requestId: string } => {
  return (
    payload !== null &&
    typeof payload === 'object' &&
    'requestId' in payload &&
    typeof payload.requestId === 'string' &&
    'error' in payload &&
    isParseableError(payload.error)
  );
};

export const getLegacyDescription = (error: ParseableError) => {
  if (!isNeo4jError(error)) {
    return undefined;
  }

  const { code, message } = error;
  return code === ClientErrors.Schema.ConstraintValidationFailed.code
    ? getConstraintValidationErrorDescription(message)
    : legacyNeo4jErrorDescriptions[code];
};

const getConstraintValidationErrorDescription = (message: string): string => {
  const existenceMessageGroups = message.match(/([^`]+)`$/);
  const uniquenessMessageGroups = message.match(/.* property (.*?) = (.*)/);
  if (existenceMessageGroups != null && existenceMessageGroups.length > 1) {
    return `This property is mandatory: ${existenceMessageGroups[1]}`;
  } else if (uniquenessMessageGroups != null && uniquenessMessageGroups.length > 2) {
    // @ts-expect-error uniquenessMessageGroups length is checked in if condition
    const matchPropertyName = /`(.*)`/.exec(uniquenessMessageGroups[1]);
    const trimmedPropertyName =
      matchPropertyName != null && matchPropertyName.length > 1 ? matchPropertyName[1] : uniquenessMessageGroups[1];
    return `A node already exists with ${trimmedPropertyName} ${uniquenessMessageGroups[2]}`;
  }
  return 'Violation of an existence or uniqueness constraint';
};
